// ── Early Bird 周报草稿生成面板 ────────────────────────────────────────
// 选品逻辑见 src/services/earlyBirdFeed.js 的 fetchWeeklyDigestCandidates
// （最近收录 + 真实信号：Demo 存活 / GitHub 活跃），文案组装见
// src/services/earlyBirdDigest.js（纯函数，可单测）。这里只管 UI 状态：
// 勾选哪些项目、Twitter/Markdown 格式切换、复制反馈。
import { useState, useEffect } from 'react';
import { Sparkles, Download, X } from 'lucide-react';
import { fetchWeeklyDigestCandidates } from '../services/earlyBirdFeed';
import { SIGNAL_LABELS, buildTwitterDraft, buildMarkdownDraft } from '../services/earlyBirdDigest';

const C = {
  surface: '#ffffff', border: '#e4e4e7', text: '#18181b',
  muted: '#71717a', faint: '#f4f4f5',
};

const SIGNAL_COLORS = {
  site_alive: { bg: '#e6f1fb', text: '#0c447c' },
  github_active: { bg: '#eaf3de', text: '#27500a' },
};

const DIGEST_DAYS = 7;
const MAX_SELECTED = 8;

function weekLabel() {
  const end = new Date();
  const start = new Date(end.getTime() - (DIGEST_DAYS - 1) * 86400000);
  const fmt = (d) => `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return `${start.getFullYear()}-${fmt(start)} ~ ${fmt(end)}`;
}

export function EarlyBirdWeeklyDigest({ onClose, lang = 'zh' }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [format, setFormat] = useState('tw');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchWeeklyDigestCandidates({ days: DIGEST_DAYS }).then((rows) => {
      setCandidates(rows);
      // 默认勾选前 N 条有信号的（信号项已经在 fetchWeeklyDigestCandidates 里排前面）
      setSelected(new Set(rows.filter(r => r.signal).slice(0, MAX_SELECTED).map(r => r.id)));
      setLoading(false);
    });
  }, []);

  const selectedProjects = candidates.filter(p => selected.has(p.id));
  const label = weekLabel();
  const origin = window.location.origin;
  const draft = selectedProjects.length === 0
    ? ''
    : format === 'tw'
      ? buildTwitterDraft(selectedProjects, { origin, weekLabel: label, lang })
      : buildMarkdownDraft(selectedProjects, { origin, weekLabel: label, lang });

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const copy = () => {
    navigator.clipboard.writeText(draft).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  const siteAliveCount = candidates.filter(p => p.signal === 'site_alive').length;
  const githubActiveCount = candidates.filter(p => p.signal === 'github_active').length;

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 18px', marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
          {lang === 'en' ? 'Weekly digest draft' : '本周精选草稿'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: C.muted }}>{label}</span>
          <button onClick={onClose} aria-label="close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex' }}>
            <X size={14} strokeWidth={2} />
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ color: C.muted, fontSize: 12, padding: '12px 0' }}>{lang === 'en' ? 'Loading…' : '加载中…'}</div>
      ) : candidates.length === 0 ? (
        <div style={{ color: C.muted, fontSize: 12, padding: '12px 0' }}>
          {lang === 'en' ? 'No projects collected in the last 7 days.' : '最近 7 天没有新收录的项目。'}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, marginBottom: 14 }}>
            <div style={{ background: C.faint, borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>{lang === 'en' ? 'New this week' : '本周新收录'}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{candidates.length}</div>
            </div>
            <div style={{ background: C.faint, borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>{lang === 'en' ? 'Live demo' : 'Demo 存活'}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{siteAliveCount}</div>
            </div>
            <div style={{ background: C.faint, borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>{lang === 'en' ? 'Active on GitHub' : 'GitHub 活跃'}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{githubActiveCount}</div>
            </div>
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 8 }}>
            {lang === 'en' ? 'Pick projects to include' : '自动选中强信号项目（可取消勾选）'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14, maxHeight: 260, overflowY: 'auto' }}>
            {candidates.map((p) => {
              const sig = SIGNAL_LABELS[p.signal];
              const sigColor = SIGNAL_COLORS[p.signal];
              return (
                <label key={p.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', background: C.faint, borderRadius: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} style={{ marginTop: 3 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 2 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{p.project_name}</span>
                      <span style={{ fontSize: 10, color: '#a1a1aa' }}>{p.tax_category || '—'} · {p.organization}</span>
                      {sig && (
                        <span style={{ background: sigColor.bg, color: sigColor.text, fontSize: 9, padding: '1px 7px', borderRadius: 999 }}>
                          {lang === 'en' ? sig.en : sig.zh}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted }}>{p.one_liner || p.description || ''}</div>
                  </div>
                </label>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <button onClick={() => setFormat('tw')} style={{
              flex: 1, padding: 6, borderRadius: 6, fontSize: 11, cursor: 'pointer',
              border: `1px solid ${format === 'tw' ? C.text : C.border}`,
              background: format === 'tw' ? C.text : C.surface, color: format === 'tw' ? '#fff' : C.text,
            }}>{lang === 'en' ? 'Twitter thread' : 'Twitter 长文'}</button>
            <button onClick={() => setFormat('md')} style={{
              flex: 1, padding: 6, borderRadius: 6, fontSize: 11, cursor: 'pointer',
              border: `1px solid ${format === 'md' ? C.text : C.border}`,
              background: format === 'md' ? C.text : C.surface, color: format === 'md' ? '#fff' : C.text,
            }}>Markdown</button>
          </div>

          <div style={{ background: C.faint, borderRadius: 8, padding: '12px 14px', marginBottom: 10, fontSize: 12, lineHeight: 1.7, color: C.text, whiteSpace: 'pre-wrap' }}>
            {draft || (lang === 'en' ? 'Select at least one project.' : '至少勾选一个项目。')}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={copy} disabled={!draft} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 999,
              border: `1px solid ${C.border}`, background: C.surface, color: copied ? '#16a34a' : C.text,
              fontSize: 11, cursor: draft ? 'pointer' : 'default',
            }}>
              <Download size={12} strokeWidth={2} />
              {copied ? (lang === 'en' ? 'Copied' : '已复制') : (lang === 'en' ? 'Copy draft' : '复制文案')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function OpenDigestButton({ onClick, lang = 'zh' }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 999,
      border: `1px solid ${C.text}`, background: C.text, color: '#fff', fontSize: 12, cursor: 'pointer',
    }}>
      <Sparkles size={13} strokeWidth={2} />
      {lang === 'en' ? 'Generate weekly digest' : '生成本周精选'}
    </button>
  );
}
